import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link as LinkRouter } from "react-router-dom";
import logo from "../../images/logo-cliente-recurrente-pec.svg";
import { waLink, WA_MSG } from "./wa";

const Wrap = styled.footer`
  position: relative;
  background: var(--dark);
  overflow: hidden;
`;

/* Dramatic glow behind the closing statement */
const Glow = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(48% 60% at 12% 0%, rgba(var(--accent-rgb), 0.20), transparent 60%),
    radial-gradient(40% 50% at 90% 110%, rgba(var(--accent-rgb), 0.10), transparent 60%);
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 5rem 1.5rem 2.5rem;

  @media (min-width: 768px) { padding: 7rem 2rem 3rem; }
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: start;
  padding-bottom: 4rem;
  border-bottom: 1px solid var(--dark-rule);

  @media (min-width: 880px) {
    grid-template-columns: 1.3fr 1fr;
    gap: 4rem;
  }
`;

const Statement = styled(motion.div)`
  h2 {
    font-family: var(--sans);
    font-weight: 700;
    font-size: clamp(2.6rem, 6.5vw, 5rem);
    line-height: 1.0;
    letter-spacing: -0.035em;
    margin: 0;
    color: var(--dark-ink);

    .accent {
      font-family: var(--serif);
      font-weight: 400;
      font-style: italic;
      letter-spacing: -0.01em;
      color: var(--accent);
    }
  }

  p {
    font-family: var(--sans);
    font-size: 1.05rem;
    line-height: 1.6;
    color: var(--dark-ink-2);
    margin: 1.6rem 0 0;
    max-width: 42ch;
  }
`;

const Ctas = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  align-items: stretch;

  @media (min-width: 480px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }

  @media (min-width: 880px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Primary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--accent);
  color: #fff;
  font-family: var(--sans);
  font-size: 1.15rem;
  font-weight: 600;
  padding: 22px 36px;
  border-radius: 999px;
  text-decoration: none;
  transition: background 200ms ease, box-shadow 280ms ease;
  letter-spacing: -0.008em;
  white-space: nowrap;
  box-shadow: 0 18px 50px -20px rgba(var(--accent-rgb), 0.8);

  &:hover {
    background: var(--accent-2);
    box-shadow: 0 22px 60px -20px rgba(var(--accent-rgb), 0.9);
  }

  svg { width: 18px; height: 18px; }
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding-top: 2.5rem;

  @media (min-width: 880px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 3rem;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;

  img {
    width: 24px;
    filter: brightness(0) invert(0.92);
  }

  span {
    font-family: var(--sans);
    font-weight: 600;
    font-size: 1rem;
    color: var(--dark-ink);
    letter-spacing: -0.01em;
  }
`;

const Links = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;

  a, span {
    font-family: var(--sans);
    font-size: 0.9rem;
    color: var(--dark-ink-2);
    text-decoration: none;
    transition: color 200ms ease;
  }

  a:hover { color: var(--dark-ink); }
`;

const Fine = styled.div`
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--dark-ink-3);
  line-height: 1.7;

  @media (min-width: 880px) {
    text-align: right;
    max-width: 32ch;
  }
`;

export default function FooterChat() {
  return (
    <Wrap>
      <Glow />
      <Inner>
        <Top>
          <Statement
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <h2>
              Hagamos que tus clientes <span className="accent">vuelvan</span>
            </h2>
            <p>
              Una conversación corta para entender tu e-commerce, ver si encaja,
              y mostrarte ejemplos reales.
            </p>
          </Statement>

          <Ctas>
            <Primary href={waLink(WA_MSG.demo)} target="_blank" rel="noreferrer">
              Agendar demo
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Primary>
          </Ctas>
        </Top>

        <Bottom>
          <Brand>
            <img src={logo} alt="" />
            <span>Cliente Recurrente</span>
          </Brand>
          <Links>
            <LinkRouter to="/">Inicio</LinkRouter>
            <span>Blog</span>
            <LinkRouter to="/terminos-y-condiciones">Términos</LinkRouter>
            <LinkRouter to="/politica-de-privacidad">Privacidad</LinkRouter>
          </Links>
          <Fine>
            Hecho en Chile con amor ☕ y 🐶
          </Fine>
        </Bottom>
      </Inner>
    </Wrap>
  );
}
